import { Injectable, signal } from '@angular/core';

interface VendorPrefixedFullscreenElement extends HTMLElement {
  mozRequestFullScreen?: () => void;
  msRequestFullscreen?: () => void;
  webkitRequestFullscreen?: () => void;
}

interface VendorPrefixedFullscreenDocument extends Document {
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  mozCancelFullScreen?: () => void;
  msExitFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
}

const FULLSCREEN_CHANGE_EVENTS = [
  'fullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange',
  'webkitfullscreenchange',
];

@Injectable({
  providedIn: 'root',
})
export class FullscreenService {
  readonly isFullscreen = signal(FullscreenService.isFullscreenActive());

  constructor() {
    FULLSCREEN_CHANGE_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, this.onFullscreenChange);
    });
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  toggle(): void {
    if (FullscreenService.isFullscreenActive()) {
      FullscreenService.exitFullscreen();
    } else {
      FullscreenService.requestFullscreen();
    }
  }

  private static isFullscreenActive(): boolean {
    const doc = document as VendorPrefixedFullscreenDocument;
    return Boolean(
      document.fullscreenElement ??
        doc.mozFullScreenElement ??
        doc.msFullscreenElement ??
        doc.webkitFullscreenElement,
    );
  }

  private static requestFullscreen(): void {
    const element = document.documentElement as VendorPrefixedFullscreenElement;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (element.requestFullscreen) {
      element.requestFullscreen().catch((error: unknown) => {
        console.error('Error entering fullscreen:', error);
      });
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    }
  }

  private static exitFullscreen(): void {
    const doc = document as VendorPrefixedFullscreenDocument;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (document.exitFullscreen) {
      document.exitFullscreen().catch((error: unknown) => {
        console.error('Error exiting fullscreen:', error);
      });
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    }
  }

  private readonly onFullscreenChange = (): void => {
    this.isFullscreen.set(FullscreenService.isFullscreenActive());
  };
}
